/**
 * S07 — WHITE TRUFFLE (the label)
 *
 * 3 product plinths in a dark gallery: OIL, RISERVA, KNIFE TALK.
 * OIL uses HF#10 (real product photography). RISERVA + KNIFE TALK use
 * typographic placeholders until their Higgsfield shots land.
 */

import { MonumentType } from "@/components/primitives/MonumentType";
import { sectionCopy } from "@/lib/voice";
import oilSrc from "@/assets/higgsfield/hf-10-product-oil.webp";
import wineSrc from "@/assets/higgsfield/hf-11-product-wine.webp";
import knifeSrc from "@/assets/higgsfield/hf-12-product-knife.webp";

type Product = {
  name: string;
  no: string;
  image: string;
  category: string;
};

const products: Product[] = [
  {
    name: "WHITE TRUFFLE",
    no: "NO. 001",
    image: oilSrc,
    category: "OIL / 375ML",
  },
  {
    name: "RISERVA",
    no: "NO. 002",
    image: wineSrc,
    category: "RED WINE / 2026",
  },
  {
    name: "KNIFE TALK",
    no: "NO. 003",
    image: knifeSrc,
    category: "HAND-FORGED STEEL",
  },
];

export function S07_TheLabel() {
  const copy = sectionCopy.s07;

  return (
    <section
      id="s07"
      className="section"
      style={{
        backgroundColor: "var(--oil)",
        color: "var(--bone)",
        flexDirection: "column",
        padding: "12vh var(--space-lg)",
      }}
    >
      {/* Concrete gallery backdrop */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse at center 65%, rgba(60,50,40,0.35) 0%, rgba(5,5,5,0.95) 75%), linear-gradient(180deg, rgba(5,5,5,0.85) 0%, rgba(26,24,20,0.75) 100%)",
          zIndex: 0,
        }}
      />

      <MonumentType word={copy.monument} theme="ink" />

      {/* Header */}
      <div
        style={{
          position: "relative",
          zIndex: 3,
          textAlign: "center",
          marginBottom: "var(--space-xl)",
        }}
      >
        <div
          className="type-meta-sm"
          style={{
            color: "var(--gold)",
            marginBottom: "var(--space-md)",
            letterSpacing: "var(--ls-meta-wide)",
          }}
        >
          {copy.meta} — THE LABEL
        </div>
        <h2
          className="type-hero-lg"
          style={{ color: "var(--bone)", margin: 0 }}
        >
          {copy.hero}
        </h2>
        {copy.subhero && (
          <p
            className="type-voice"
            style={{
              color: "var(--fog)",
              marginTop: "var(--space-md)",
              fontSize: "clamp(1.25rem, 1.6vw, 1.75rem)",
              fontStyle: "italic",
            }}
          >
            {copy.subhero}
          </p>
        )}
      </div>

      {/* Plinth row */}
      <div
        style={{
          position: "relative",
          zIndex: 3,
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "var(--space-xl)",
          width: "min(1200px, 88vw)",
          alignItems: "end",
        }}
      >
        {products.map((product) => (
          <div key={product.name} style={{ textAlign: "center" }}>
            {/* Plinth shaft */}
            <div
              style={{
                position: "relative",
                height: "58vh",
                background:
                  "linear-gradient(180deg, rgba(26,24,20,0.4) 0%, rgba(5,5,5,0.92) 100%)",
                border: "1px solid rgba(242,193,78,0.25)",
                overflow: "hidden",
                boxShadow:
                  "0 40px 80px rgba(0,0,0,0.6), inset 0 0 60px rgba(0,0,0,0.6)",
              }}
            >
              {/* Real product photograph */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  backgroundImage: `url(${product.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  filter: "contrast(1.08) brightness(0.95)",
                }}
              />
            </div>
            {/* Plaque */}
            <div
              style={{
                borderTop: "1px solid rgba(242,193,78,0.5)",
                paddingTop: "var(--space-md)",
                marginTop: "var(--space-md)",
              }}
            >
              <div
                className="type-meta-lg"
                style={{
                  color: "var(--gold)",
                  letterSpacing: "var(--ls-meta-wide)",
                }}
              >
                {product.name}
              </div>
              <div
                className="type-meta-sm"
                style={{
                  color: "var(--fog)",
                  marginTop: "var(--space-xs)",
                }}
              >
                {product.no} · {product.category}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
